import type {
  RepositoryIntelligenceLifecycleState,
  RepositoryIntelligenceRegistration,
  RepositoryIntelligenceStatus,
} from '@ashniva/types';

type Registration = RepositoryIntelligenceRegistration['registration'];

/**
 * Where a repository is with Repository Intelligence, as the screens show it (MASTER-SPEC §14.4): one of the states,
 * with what each needs to say. Derived from the status (`GET …/intelligence`) and, when the status cannot say — the
 * repository is not served yet, or it failed — the registration Desk asked for (`GET …/intelligence/registration`).
 */
export type RepositoryStateKind =
  | 'ready'
  | 'stale'
  | 'indexing'
  | 'pending'
  | 'provisioning'
  | 'not-registered'
  | 'rejected'
  | 'suspended'
  | 'failed';

export interface RepositoryState {
  kind: RepositoryStateKind;
  /** Pending and provisioning: which of `REGISTRATION_STEPS` it is at. */
  step?: number;
  /** Since when: stale since, indexing started, failed or suspended since. */
  since?: string | null;
  indexedCommit?: string | null;
  indexedAt?: string | null;
  /** A failure code, for `failureReason`. */
  reasonCode?: string | null;
  /** A rejection's reason: the operator's own words, shown as plain text only. */
  decisionReason?: string | null;
  /** Ready, and bringing itself up to date right now. */
  syncing?: boolean;
}

/** The registration flow, in order (P14-03): request → approval → copy and check → index → ready. */
export const REGISTRATION_STEPS = [
  'Requested',
  'Approved',
  'Copying and checking',
  'Indexing',
  'Ready',
] as const;

const STEP_OF_LIFECYCLE: Partial<Record<RepositoryIntelligenceLifecycleState, number>> = {
  approved: 1,
  cloning: 2,
  verifying: 2,
  indexing: 3,
};

export function repositoryState(
  status: RepositoryIntelligenceStatus | null,
  registration: Registration | null,
): RepositoryState {
  if (status) {
    return fromStatus(status, registration);
  }
  return registration ? fromRegistration(registration) : { kind: 'not-registered' };
}

function freshness(status: RepositoryIntelligenceStatus) {
  const ingestion = status.index.latestIngestion;
  return {
    indexedCommit: status.sync?.indexedCommit ?? ingestion?.commit ?? null,
    indexedAt:
      status.sync?.lastSuccessAt ??
      (ingestion?.status === 'succeeded' ? ingestion.finishedAt : null),
  };
}

function fromStatus(
  status: RepositoryIntelligenceStatus,
  registration: Registration | null,
): RepositoryState {
  const lifecycle = status.lifecycle;
  if (!lifecycle) {
    // An older Repository Intelligence reports no lifecycle: its checkout says whether it serves.
    return status.checkout.available
      ? { kind: 'ready', ...freshness(status) }
      : { kind: 'failed', reasonCode: `checkout_${status.checkout.state}` };
  }
  switch (lifecycle.state) {
    case 'ready':
    case 'syncing':
      return { kind: 'ready', syncing: lifecycle.state === 'syncing', ...freshness(status) };
    case 'degraded':
      return {
        kind: 'stale',
        since: status.sync?.staleSince ?? lifecycle.since,
        reasonCode: status.sync?.lastErrorCode ?? null,
        ...freshness(status),
      };
    case 'indexing':
      return {
        kind: 'indexing',
        step: 3,
        since: status.index.latestIngestion?.startedAt ?? lifecycle.since,
      };
    case 'approved':
    case 'cloning':
    case 'verifying':
      return {
        kind: 'provisioning',
        step: STEP_OF_LIFECYCLE[lifecycle.state],
        since: lifecycle.since,
      };
    case 'suspended':
      return {
        kind: 'suspended',
        since: lifecycle.since,
        reasonCode: status.sync?.lastErrorCode ?? null,
      };
    case 'failed':
      return {
        kind: 'failed',
        since: lifecycle.since,
        reasonCode: registration?.errorCode ?? status.sync?.lastErrorCode ?? null,
      };
    case 'unlinked':
    case 'purging':
    case 'purged':
      // Taken back out: it can be requested again.
      return { kind: 'not-registered' };
  }
}

function fromRegistration(registration: Registration): RepositoryState {
  switch (registration.status) {
    case 'pending':
      return { kind: 'pending', step: 0, since: registration.createdAt };
    case 'approved':
      return { kind: 'provisioning', step: 1, since: registration.decision?.at ?? null };
    case 'provisioning': {
      const lifecycle = registration.lifecycle?.state;
      const step = (lifecycle && STEP_OF_LIFECYCLE[lifecycle]) ?? 2;
      return {
        kind: step === 3 ? 'indexing' : 'provisioning',
        step,
        since: registration.lifecycle?.since ?? null,
      };
    }
    case 'active':
      // Served, though the status could not say more just now.
      return { kind: 'ready' };
    case 'rejected':
      return {
        kind: 'rejected',
        since: registration.decision?.at ?? null,
        decisionReason: registration.decision?.reason ?? null,
      };
    case 'failed':
      return { kind: 'failed', reasonCode: registration.errorCode };
    case 'cancelled':
    case 'unlinked':
      return { kind: 'not-registered' };
  }
}

/** The repository is served: searching, reading and asking can work. */
export function isServed(state: RepositoryState): boolean {
  return state.kind === 'ready' || state.kind === 'stale';
}

/** A registration in flight, which is worth following until it settles. */
export function isInFlight(state: RepositoryState): boolean {
  return state.kind === 'pending' || state.kind === 'provisioning' || state.kind === 'indexing';
}
